import type { AppEntry } from "../registry/apps";

export const QUEST_GOAL = 9000;

/**
 * 31 Dec 2026. `month` is 0-based, so 11 is December.
 * Day math uses `questDeadlineDay()`, not `Date` in the machine timezone.
 */
export const QUEST_DEADLINE = { year: 2026, month: 11, day: 31 } as const;

/** One clock for every visitor. `shippedAt` dates are this calendar. */
export const QUEST_TIME_ZONE = "America/Los_Angeles";

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export type PaceStatus = "empty" | "behind" | "on_pace" | "ahead" | "complete";

export type Progress = {
  count: number;
  remaining: number;
  percent: number;
  daysLeft: number;
  needPerDay: number;
  actualPerDay: number | null;
  shippedToday: number;
  streak: number;
  paceStatus: PaceStatus;
  /** Straight-line fill for today, from the first ship through the deadline. */
  targetPercent: number | null;
  live: AppEntry[];
};

export function isLive(app: AppEntry): boolean {
  return app.status === "live" && app.example !== true;
}

export function isPlaceholder(app: AppEntry): boolean {
  return app.example === true || app.status !== "live";
}

export function questDeadlineDay(): string {
  const { year, month, day } = QUEST_DEADLINE;
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Calendar day of `now` in the quest timezone, `YYYY-MM-DD`. */
export function questDay(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: QUEST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;
  const year = value("year");
  const month = value("month");
  const day = value("day");
  if (!year || !month || !day) return "1970-01-01";
  return `${year}-${month}-${day}`;
}

function addCalendarDays(iso: string, delta: number): string {
  const match = DATE_ONLY.exec(iso);
  const year = Number(match?.[1]);
  const month = Number(match?.[2]);
  const day = Number(match?.[3]);
  if (!year || !month || !day) return iso;
  const utc = new Date(Date.UTC(year, month - 1, day + delta));
  return utc.toISOString().slice(0, 10);
}

/** Whole calendar days from `startIso` to `endIso`. Negative when `endIso` is earlier. */
function calendarDaysBetween(startIso: string, endIso: string): number {
  const start = Date.parse(`${startIso}T00:00:00Z`);
  const end = Date.parse(`${endIso}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return Number.NaN;
  return Math.round((end - start) / 86_400_000);
}

/**
 * Ship day for the HUD.
 * `YYYY-MM-DD` is already a quest calendar day — parsing it as an instant would
 * land on the previous Pacific evening. A timestamp is an instant in Pacific time.
 */
export function shippedDay(app: Pick<AppEntry, "shippedAt">): string {
  const raw = app.shippedAt.trim();
  if (DATE_ONLY.test(raw)) return raw;
  const instant = Date.parse(raw);
  if (Number.isNaN(instant)) return raw.slice(0, 10);
  return questDay(new Date(instant));
}

/** Days from the quest-timezone today through 31 Dec 2026, inclusive. 0 after the deadline. */
export function daysRemainingInclusive(now = new Date()): number {
  const today = questDay(now);
  const end = questDeadlineDay();
  if (today > end) return 0;
  const span = calendarDaysBetween(today, end);
  if (!Number.isFinite(span)) return 0;
  return span + 1;
}

export function computeStreak(
  live: readonly Pick<AppEntry, "shippedAt">[],
  now = new Date(),
): number {
  const days = new Set(live.map(shippedDay));
  if (days.size === 0) return 0;

  let cursor = questDay(now);
  if (!days.has(cursor)) cursor = addCalendarDays(cursor, -1);

  let streak = 0;
  while (days.has(cursor)) {
    streak += 1;
    cursor = addCalendarDays(cursor, -1);
  }
  return streak;
}

function earliestShipDay(live: readonly Pick<AppEntry, "shippedAt">[]): string | null {
  let first: string | null = null;
  for (const app of live) {
    const day = shippedDay(app);
    if (!DATE_ONLY.test(day)) continue;
    if (first === null || day < first) first = day;
  }
  return first;
}

function paceStatusFor(count: number, daysLeft: number, elapsed: number | null): PaceStatus {
  if (count >= QUEST_GOAL) return "complete";
  if (daysLeft === 0) return "behind";
  if (elapsed === null || elapsed < 1) return "empty";
  // ponytail: flag matches the red marker (count/elapsed vs goal/span). Comparing
  // pace to remaining/daysLeft counts today twice and can say ON PACE while the
  // fill is still left of the marker. A partial-day weight needs an hour on
  // shippedAt; date-only stamps have none.
  const span = elapsed + daysLeft - 1;
  const covered = count * span;
  const line = QUEST_GOAL * elapsed;
  if (covered * 10 >= line * 11) return "ahead";
  if (covered >= line) return "on_pace";
  return "behind";
}

export function computeProgress(apps: readonly AppEntry[], now = new Date()): Progress {
  const live = apps.filter(isLive);
  const count = live.length;
  const remaining = Math.max(0, QUEST_GOAL - count);
  const daysLeft = daysRemainingInclusive(now);
  const percent = (count / QUEST_GOAL) * 100;
  const needPerDay =
    daysLeft === 0 ? (remaining === 0 ? 0 : Number.POSITIVE_INFINITY) : remaining / daysLeft;
  const today = questDay(now);
  const shippedToday = live.filter((app) => shippedDay(app) === today).length;
  const streak = computeStreak(live, now);

  const first = earliestShipDay(live);
  let elapsed: number | null = null;
  let actualPerDay: number | null = null;
  let targetPercent: number | null = null;

  if (count > 0 && first && first <= today) {
    const days = calendarDaysBetween(first, today) + 1;
    if (Number.isFinite(days) && days >= 1) {
      elapsed = days;
      actualPerDay = count / days;
      const span = days + Math.max(0, daysLeft - 1);
      targetPercent = span === 0 ? 100 : Math.min(100, (days / span) * 100);
    }
  }

  return {
    count,
    remaining,
    percent,
    daysLeft,
    needPerDay,
    actualPerDay,
    shippedToday,
    streak,
    paceStatus: paceStatusFor(count, daysLeft, elapsed),
    targetPercent,
    live,
  };
}
